/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import flow from 'lodash.flow';
import { getLogger } from 'src/core/services/telemetry/telemetryLogger';
import { HandlerGroupFactoryDeps, HandlerParams } from 'src/core/types/handler';
import { addDirtyState } from 'src/core/utils/decorators/addDirtyState';
import { getHierarchicalEntityService } from 'src/luis/core/services/entities/hierarchicalEntityService';
import { getEntityHandlerGroupUtils } from 'src/luis/projects/entities/utils/entityHelpers';
import { LuisId } from 'src/luis/types/Resource';
import { HierarchicalEntityStore } from 'src/luis/types/stores/entities/HierarchicalEntityStore';

export type HierarchicalEntityHandlerGroup = ReturnType<typeof createHierarchicalEntityHandlerGroup>;

/**
 * Represents a handler group for hierarchical entity operations.
 *
 * @link Check out dispatcher.md
 *
 * @param dependencies The handler group dependencies.
 */
export const createHierarchicalEntityHandlerGroup = (dependencies: HandlerGroupFactoryDeps) => {
    const { rootStore } = dependencies;

    const { addOrReplaceEntityStore, getAppVersionStore, getEntityStore } = getEntityHandlerGroupUtils(rootStore);

    const logger = getLogger();
    /**
     * Loads a given hierarchical entity.
     *
     * @param id The id of the hierarchical entity to load.
     */
    const loadHierarchicalEntity = async ({ id, context }: HandlerParams<Pick<HierarchicalEntityStore, 'id'>>) => {
        const { appId, versionId } = context;
        const hierarchicalEntityService = getHierarchicalEntityService({ context });
        const logTimedActivity = logger.createTimedActivityLogger<HierarchicalEntityStore>('LoadHierarchicalEntity', () => ({
            entityId: id
        }));
        const hierarchicalEntityStore = await logTimedActivity(hierarchicalEntityService.getEntity({ id }));

        addOrReplaceEntityStore({ appId, versionId }, hierarchicalEntityStore);
    };

    /**
     * Deletes a give hierarchical entity.
     *
     * @param id The id of the hierarchical entity to delete.
     */
    const deleteHierarchicalEntity = async ({ id, context, tracker }: HandlerParams<Pick<HierarchicalEntityStore, 'id'>>) =>
        flow(async () => {
            if (tracker) {
                tracker.setContext(id);
            }

            const { appId, versionId } = context;
            const hierarchicalEntityService = getHierarchicalEntityService({ context });
            const logTimedActivity = logger.createTimedActivityLogger('DeleteHierarchicalEntity', () => ({ entityId: id }));
            await logTimedActivity(hierarchicalEntityService.deleteEntity({ id }));

            getAppVersionStore({ appId, versionId }).entityStoreHouse.removeById(id);
        }, addDirtyState({ appId: context.appId, versionId: context.versionId, rootStore }))();

    /**
     * Renames a given entity.
     *
     * @param id The id of the entity to update.
     * @param name The hierarchical entity new name.
     */
    const renameHierarchicalEntity = async ({
        id,
        name: newName,
        children,
        context
    }: HandlerParams<Pick<HierarchicalEntityStore, 'name' | 'id' | 'children'>>) =>
        flow(async () => {
            const { appId, versionId } = context;
            const hierarchicalEntityService = getHierarchicalEntityService({ context });
            const logTimedActivity = logger.createTimedActivityLogger('RenameHierarchicalEntity', () => ({ entityId: id, newName }));
            await logTimedActivity(hierarchicalEntityService.renameEntity({ id, name: newName, children: children.map(c => c.name) }));

            const entityStore = getEntityStore({ appId, versionId, entityId: id });
            entityStore.rename(newName);
        }, addDirtyState({ appId: context.appId, versionId: context.versionId, rootStore }))();

    /**
     * Renames a given hierarchical entity child.
     *
     * @param id The id of the entity to update.
     * @param name The hierarchical entity new name.
     */
    const renameHierarchicalChildEntity = async ({
        id,
        childId,
        name,
        context
    }: HandlerParams<Pick<HierarchicalEntityStore, 'id'> & { childId: LuisId; name: string }>) =>
        flow(async () => {
            const { appId, versionId } = context;
            const hierarchicalEntityService = getHierarchicalEntityService({ context });
            const logTimedActivity = logger.createTimedActivityLogger('RenameHierarchicalChildEntity', () => ({
                entityId: id,
                childId,
                name
            }));
            await logTimedActivity(hierarchicalEntityService.renameChildEntity({ id, childId, name }));

            const entityStore = getEntityStore({ appId, versionId, entityId: id });
            (<HierarchicalEntityStore>entityStore).renameChild(childId, name);
        }, addDirtyState({ appId: context.appId, versionId: context.versionId, rootStore }))();

    /**
     * Deletes children entity from a given hierarchical entity.
     *
     * @param id The hierarchical entity id.
     * @param childIds The ids of the children to delete.
     */
    const deleteChildrenEntityForHierarchicalEntity = async ({
        id,
        childIds,
        context
    }: HandlerParams<Pick<HierarchicalEntityStore, 'id'> & { childIds: LuisId[] }>) =>
        flow(async () => {
            const { appId, versionId } = context;
            const hierarchicalEntityService = getHierarchicalEntityService({ context });
            const logTimedActivity = logger.createTimedActivityLogger<void[]>('DeleteChildrenEntityForHierarchicalEntity', () => ({
                entityId: id,
                childIds
            }));
            const servicePromises = childIds.map(childId => hierarchicalEntityService.deleteChildEntity({ id, childId }));
            await logTimedActivity(Promise.all(servicePromises));

            childIds.forEach(async childId => {
                getEntityStore<HierarchicalEntityStore>({ appId, versionId, entityId: id }).removeChildById(childId);
            });
        }, addDirtyState({ appId: context.appId, versionId: context.versionId, rootStore }))();

    return {
        loadHierarchicalEntity,
        deleteHierarchicalEntity,
        renameHierarchicalEntity,
        renameHierarchicalChildEntity,
        deleteChildrenEntityForHierarchicalEntity
    };
};
